"use client";

import { css } from "@emotion/react";
import { useEffect, useState, useRef } from "react";
import posthog from "posthog-js";
import GetStartedButton from "../Buttons/GetStartedButton";
import { iconColor, lightBlueColor } from "@/constants/colors";
import { H6Css, Heading } from "../Typography";
import { mq } from "@/constants/breakpoints";
import { CloseIconButton } from "../CloseIconButton";
import { useWindowSize } from "@/hooks/useWindowSize";

export function BlogSlideUpBanner() {
  const { isXs } = useWindowSize();

  const [isMounted, setIsMounted] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    timeoutRef.current = window.setTimeout(() => {
      setIsMounted(true);
      posthog.capture("JWMARKETING_BLOG_SLIDE_UP_OPENED");
    }, 6000);

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleDismiss = () => {
    posthog.capture("JWMARKETING_BLOG_SLIDE_UP_DISMISSED");
    setIsMounted(false);
  };

  return (
    <div
      css={css`
        position: fixed;
        bottom: ${isMounted ? "0" : "-150px"};
        left: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: ${lightBlueColor};
        transition: bottom 0.5s ease-in-out;
        pointer-events: ${isMounted ? "auto" : "none"};
        gap: 20px;
        height: fit-content;
        padding: 12px 7px 12px 16px;
        ${mq["sm"]} {
          bottom: ${isMounted ? "0" : "-100px"};
          height: 50px;
          padding: 20px 30px 20px 40px;
        }
        ${mq["lg"]} {
          height: 50px;
          padding: 24px 30px 24px 40px;
        }
      `}
    >
      <div
        css={css`
          width: 100%;
          display: flex;
          justify-content: center;
        `}
      >
        <div
          css={css`
            height: fit-content;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 10px;
            width: 100%;
            max-width: 1000px;
            ${mq["sm"]} {
              gap: 20px;
            }
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <Heading
              level={6}
              css={css`
                ${H6Css}
                color: white;
                font-size: 16px;
                font-weight: normal;
                margin-bottom: 0;
                ${mq["sm"]} {
                  font-size: 18px;
                }
                ${mq["lg"]} {
                  font-size: 24px;
                }
              `}
            >
              Ready to start building your website?
            </Heading>
            <Heading
              level={6}
              css={css`
                display: none;
                ${mq["lg"]} {
                  display: block;
                  color: white;
                  font-size: 14px;
                  font-weight: normal;
                  margin-top: 4px;
                }
              `}
            >
              Join 2,500+ sports organizations.
            </Heading>
          </div>

          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 20px;
              ${mq["sm"]} {
                gap: 17px;
              }
            `}
          >
            <GetStartedButton
              id="#blog-slide-up-button"
              css={css`
                padding: 12px 24px;
              `}
              text={isXs ? "Get Started" : undefined}
            />
          </div>
        </div>
      </div>
      <CloseIconButton iconColor={iconColor} onClose={handleDismiss} />
    </div>
  );
}
